import { ArrowPathIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ArrowsPointingInIcon, ArrowsPointingOutIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import React, { useEffect, useState } from "react";

import {
  Badge,
  Dialog,
  Input,
  Label,
  Paragraph,
  ReorderableList,
  Tooltip,
} from "@fronterahealth/frontera-ui-components";

import { useReorderGoalsMutation } from "@api/graphql/types-and-hooks";
import { ReorderIcon } from "@components/TreatmentPlan/ProgrammingGoalCard/ReorderListIcon";
import { getStatusAppearance, getStatusIcon } from "@components/TreatmentPlan/ProgrammingGoalCard/helper";
import { notifySuccess } from "@components/notifications/notifications";
import { ProgramReviewGoal, useProgrammingReviewData } from "@providers/ProgrammingReviewProvider";

interface GoalListItem {
  id: string;
  title: string;
  count: number;
  status: string;
  goalId: string;
}

interface ProgrammingGoalReviewHeaderProps {
  goalList: Array<ProgramReviewGoal>;
  collapseAll: boolean;
  setCollapseAll: React.Dispatch<React.SetStateAction<boolean>>;
  isReadOnly?: boolean;
}

export const ProgrammingGoalReviewHeader: React.FC<ProgrammingGoalReviewHeaderProps> = ({
  goalList,
  collapseAll,
  setCollapseAll,
  isReadOnly = false,
}) => {
  const {
    getProgramReviewGoalsQuery: { refetch, isFetching },
    searchGoalText,
    setSearchGoalText,
  } = useProgrammingReviewData();

  const [openReorderGoalListDialog, setOpenReorderGoalListDialog] = useState<boolean>(false);

  const [isSearchEnabled, setIsSearchEnabled] = useState<boolean>(false);

  const [reorderingGoalList, setReorderingGoalList] = useState<GoalListItem[]>([]);
  const reorderGoalListMutation = useReorderGoalsMutation({});
  const { isPending } = reorderGoalListMutation;

  useEffect(() => {
    setReorderingGoalList(
      goalList.map((item) => ({
        id: item.id,
        title: item.title,
        count: item.passageTargets?.edges.length || 0,
        status: "",
        goalId: item.goal.id,
      })),
    );
  }, [goalList]);

  const handleReorderingList = async () => {
    reorderGoalListMutation
      .mutateAsync({
        goals: reorderingGoalList.map((item, index) => ({ goal: item.goalId, rankOrder: index })),
      })
      .then((res) => {
        notifySuccess(res.reorderGoals?.message || "Reordering successfully completed!");
        refetch();
        setOpenReorderGoalListDialog(false);
      });
  };

  return (
    <div
      className={clsx(
        "flex flex-row items-center sticky top-0 z-10 gap-2 border-b border-limestone-200 -mx-2",
        isSearchEnabled ? "p-1" : "p-3",
      )}
    >
      {isSearchEnabled || searchGoalText.length > 0 ? (
        <div className="flex w-full justify-between items-center gap-1">
          <Input
            name="search"
            label=""
            className="max-w-[90%]! min-w-[90%]! -mt-2!"
            formNoValidate
            value={searchGoalText}
            placeholder="Search for keyword..."
            onChange={(e) => setSearchGoalText(e.target.value)}
            autoFocus
            inputClassName="mt-0!"
          />
          <XMarkIcon
            onClick={() => {
              setIsSearchEnabled(false);
              setSearchGoalText("");
            }}
            className="h-4 w-4 mt-2 shrink-0 cursor-pointer! text-text-primary"
          />
        </div>
      ) : (
        <div className="flex flex-1 flex-row items-center justify-between gap-2">
          <Tooltip text="Search" showInfoIcon={false}>
            <MagnifyingGlassIcon
              onClick={() => setIsSearchEnabled(true)}
              className="h-5 w-5 cursor-pointer! text-text-secondary"
            />
          </Tooltip>

          <div className="flex items-center gap-2">
            {!isReadOnly && (
              <Tooltip text="Reorder" showInfoIcon={false}>
                <ReorderIcon
                  className="h-5 w-5 cursor-pointer! text-text-secondary"
                  onClick={() => {
                    setOpenReorderGoalListDialog(true);
                  }}
                />
              </Tooltip>
            )}
            {collapseAll ? (
              <Tooltip text="Expand All" showInfoIcon={false}>
                <ArrowsPointingOutIcon
                  className="h-5 w-5 cursor-pointer text-text-secondary"
                  onClick={() => {
                    setCollapseAll(false);
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip text="Collapse All" showInfoIcon={false}>
                <ArrowsPointingInIcon
                  className="h-5 w-5 cursor-pointer text-text-secondary"
                  onClick={() => {
                    setCollapseAll(true);
                  }}
                />
              </Tooltip>
            )}
          </div>
        </div>
      )}
      <Dialog
        title={
          <Label displayType="loud" colorType="primary">
            Reorder Goals
          </Label>
        }
        showCloseButton={false}
        open={openReorderGoalListDialog}
        setOpen={() => {}}
        primaryButton={{
          text: (
            <div className="flex gap-1 w-max">
              {isPending ? <ArrowPathIcon className="h-6 w-6 animate-spin p-1 text-white" /> : null}
              Apply
            </div>
          ),

          disabled: isPending || isFetching,
          onClick: handleReorderingList,
        }}
        secondaryButton={{
          text: "Cancel",
          disabled: isPending,
          onClick: () => setOpenReorderGoalListDialog(false),
        }}
        size="3xl"
        overflowBehavior="hidden"
        bodyClassName="p-4"
        showDividers
      >
        <div className="max-h-[700px] overflow-y-auto">
          {isFetching ? (
            <div className="flex flex-col justify-center items-center h-full gap-2">
              <ArrowPathIcon className="h-6 w-6 animate-spin" />
              <Paragraph size="large" colorType="primary" displayType="loud">
                Goals Loading...
              </Paragraph>
            </div>
          ) : (
            <ReorderableList
              items={reorderingGoalList}
              onReorder={(newItems) => setReorderingGoalList(newItems)}
              renderItem={(goal) => {
                const Icon = goal.status ? getStatusIcon(goal.status) : React.Fragment;

                return (
                  <div className="flex w-full flex-row items-center justify-between gap-2 ">
                    <span className="max-w-md font-medium text-gray-800 ph-no-capture">{goal.title}</span>
                    <div className="gap- flex shrink-0 flex-row gap-1">
                      {goal.status ? (
                        <Badge
                          text={goal.status ? <Icon className="h-2 w-2" /> : null}
                          appearance={goal.status ? getStatusAppearance(goal.status) : "neutral"}
                        />
                      ) : null}
                      <span className="text-xs text-gray-500">{goal.count} targets</span>
                    </div>
                  </div>
                );
              }}
            />
          )}
        </div>
      </Dialog>
    </div>
  );
};
